// src/pages/MyImpact.js
//
// Standalone personal impact dashboard. Route: /my-impact
//
// Layout follows the same shell as Settings, FarmPulse and the legal
// pages:
//   - sticky top header with the standard back arrow (→ /farmmark)
//   - translated page title + subtitle
//   - max-w-7xl content column, px-3 / sm:px-4 / lg:px-6
//   - the actual dashboard renders from FarmMarkMyImpact so the desktop
//     FarmMark "My Impact" card and this page share one implementation.
//
// The back arrow is a plain <Link to="/farmmark"> with ArrowLeft, the same
// pattern Settings uses for "/me" and FarmMark uses for "/me". It keeps
// navigation predictable and lets the browser handle history naturally.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { errorDev } from "../utils/logger.js";
import FarmMarkMyImpact from "../components/FarmMark/FarmMarkMyImpact";

export default function MyImpact() {
  const { t, ready: i18nReady } = useTranslation(["farmMark", "common"]);

  const [renderReady, setRenderReady] = useState(false);

  // Let the i18n namespace load before rendering so the page header
  // and any nested component text resolve against the active language.
  useEffect(() => {
    if (i18nReady) setRenderReady(true);
  }, [i18nReady]);

  const safeT = (key, fallback) => {
    if (!i18nReady) return fallback || key;
    try {
      const val = t(key);
      return val === key ? fallback || key : val;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback || key;
    }
  };

  if (!renderReady) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        <div className="sticky top-0 z-30 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 h-14 flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-neutral-800 animate-pulse" />
            <div className="h-4 w-40 rounded bg-gray-200 dark:bg-neutral-800 animate-pulse" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6">
          <div className="rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6 animate-pulse space-y-4">
            <div className="h-6 w-32 rounded bg-gray-200 dark:bg-neutral-800" />
            <div className="h-4 w-56 rounded bg-gray-200 dark:bg-neutral-800" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 pb-20 sm:pb-24">
      {/* ═══ Sticky Header ═══ */}
      <div className="sticky top-0 z-30 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center h-14 gap-3">
            <Link
              to="/farmmark"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:text-gray-300 dark:hover:bg-neutral-800"
              aria-label={safeT("common:back", "Back")}
              title={safeT("common:back", "Back")}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Link>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {safeT("myImpact.title", "My Impact")}
            </h1>
          </div>
        </div>
      </div>

      {/* ═══ Content ═══ */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 lg:py-8">
        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {safeT("myImpact.subtitle", "Your personal impact dashboard")}
          </p>
        </div>

        <FarmMarkMyImpact />
      </main>
    </div>
  );
}